import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { ClassRoom, UploadedFile } from '../types';
import { ClassesAPI, QuizzesAPI, FilesAPI } from '../utils/api';

interface DataContextType {
  publicClasses: ClassRoom[];
  myClasses: ClassRoom[];
  documents: UploadedFile[];
  isDataLoaded: boolean;
  hasEntered: boolean;
  maintenanceMode: boolean;
  checkMaintenanceStatus: () => Promise<void>;
  enterWebsite: () => void;
  preloadEssentialData: (token: string) => Promise<void>; // Optimized preload - only 2-3 API calls
  clearData: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [publicClasses, setPublicClasses] = useState<ClassRoom[]>([]);
  const [myClasses, setMyClasses] = useState<ClassRoom[]>([]);
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  // Initialize hasEntered from sessionStorage
  const [hasEntered, setHasEntered] = useState<boolean>(() => {
    return sessionStorage.getItem('hasEntered') === 'true';
  });

  const checkMaintenanceStatus = async () => {
    try {
      // Import SystemAPI dynamically to avoid circular dependencies if any
      const { SystemAPI } = await import('../utils/api');
      const data = await SystemAPI.getStatus();
      setMaintenanceMode(data.maintenance_mode);
    } catch (e) {
      console.error('Failed to check system status:', e);
    }
  };

  const enterWebsite = () => {
    setHasEntered(true);
    sessionStorage.setItem('hasEntered', 'true');
  };

  // ✅ OPTIMIZED: Loads class lists + quiz lists (2 + N API calls where N = number of classes)
  // Does NOT load individual quiz details/questions to avoid full N+1 problem
  const preloadEssentialData = async (token: string) => {
    try {
      // Step 1: Fetch all classes (2 API calls)
      const [publicClassesData, myClassesData] = await Promise.all([
        ClassesAPI.listPublic(token),
        ClassesAPI.listMine(token)
      ]);

      // Step 2: Fetch quizzes for each class in parallel
      const allClasses = [...publicClassesData, ...myClassesData];
      const quizPromises = allClasses.map(cls =>
        QuizzesAPI.byClass(cls.id, token).catch(() => [])
      );

      const allQuizzes = await Promise.all(quizPromises);

      // Step 3: Attach quizzes to their respective classes
      const classQuizMap = new Map();
      allClasses.forEach((cls, index) => {
        classQuizMap.set(cls.id, allQuizzes[index] || []);
      });

      // Process public classes with quizzes
      const processedPublicClasses = publicClassesData.map(cls => ({
        ...cls,
        quizzes: (classQuizMap.get(cls.id) || []).filter((q: any) => q.published === true)
      }));

      // Process my classes with quizzes
      const processedMyClasses = myClassesData.map(cls => ({
        ...cls,
        quizzes: classQuizMap.get(cls.id) || []
      }));

      setPublicClasses(processedPublicClasses);
      setMyClasses(processedMyClasses);
      setIsDataLoaded(true);
    } catch (error) {
      console.error('Preload error:', error);
      setIsDataLoaded(false);
    }
  };


  // ⚠️ DISABLED: This function caused N+1 query problem (50-100+ API calls on load)
  // Data is now loaded lazily per page for better performance
  /*
  const prefetchAllData = async (token: string) => {
    try {
      // Parallel fetch
      const [publicClassesData, myClassesData, filesData] = await Promise.all([
        ClassesAPI.listPublic(token),
        ClassesAPI.listMine(token),
        FilesAPI.listMine(token)
      ]);

      // Process Public Classes (Attach Quizzes)
      const processedPublicClasses: ClassRoom[] = [];
      for (const cls of publicClassesData) {
        const qzs = await QuizzesAPI.byClass(cls.id, token);
        const visible = (qzs || []).filter((q: any) => q.published === true);
        processedPublicClasses.push({
          id: cls.id,
          name: cls.name,
          description: cls.description,
          quizzes: visible,
          createdAt: new Date(cls.createdAt),
          updatedAt: cls.updatedAt ? new Date(cls.updatedAt) : undefined,
        } as unknown as ClassRoom);
      }

      // Process My Classes (Attach Quizzes)
      const processedMyClasses: ClassRoom[] = [];
      for (const cls of myClassesData) {
        const quizzes = await QuizzesAPI.byClass(cls.id, token);
        const isOwner = (cls as any).accessType === "owner";

        // Filter logic from ClassesPage
        if (!isOwner && quizzes.length === 0) continue;

        processedMyClasses.push({
          id: cls.id,
          name: cls.name,
          description: cls.description,
          isPublic: cls.isPublic,
          accessType: (cls as any).accessType,
          quizzes: quizzes.map((q: any) => ({
            ...q,
            createdAt: new Date(q.createdAt),
            updatedAt: new Date(q.updatedAt),
          })),
          createdAt: new Date(cls.createdAt),
          updatedAt: cls.updatedAt ? new Date(cls.updatedAt) : undefined,
        } as unknown as ClassRoom);
      }

      // Process Documents
      const processedDocuments = filesData.map((f: any) => ({
        ...f,
        uploadedAt: new Date(f.uploadedAt)
      }));

      setPublicClasses(processedPublicClasses);
      setMyClasses(processedMyClasses);
      setDocuments(processedDocuments);
      setIsDataLoaded(true);
    } catch (error) {
      // console.error("Error pre-fetching data:", error);
      throw error;
    }
  };
  */

  const clearData = () => {
    setPublicClasses([]);
    setMyClasses([]);
    setDocuments([]);
    setIsDataLoaded(false);
    setHasEntered(false);
    sessionStorage.removeItem('hasEntered');
  };

  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  return (
    <DataContext.Provider value={{
      publicClasses,
      myClasses,
      documents,
      isDataLoaded,
      hasEntered,
      maintenanceMode,
      checkMaintenanceStatus,
      enterWebsite,
      preloadEssentialData,
      clearData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
